// ProfileCard.js
import React, { useState } from "react";
import { FiLoader } from "react-icons/fi";
import { CiEdit } from "react-icons/ci";
import { MdBlock, MdOutlinePublishedWithChanges } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import DeleteConfirmatiomModal from "../../../../Components/Common/DeleteConfirmationModal";
import { motion, AnimatePresence } from "framer-motion";
import profileIcon from "../../../../Assets/DashboardAssets/profileIcon.png";
import ProtectedSection from "../../../../Routes/ProtectedRoutes/ProtectedSection";
import { PERMISSIONS } from "../../../../config/permission";
import ProtectedAction from "../../../../Routes/ProtectedRoutes/ProtectedAction";
import { activeHelper, deactiveHelper } from "../../../../Store/Slices/Transportation/Helper/helper.action";
import { useTranslation } from "react-i18next";

const ProfileHelperCard = ({ profile, onClick, editUser }) => {
  const dispatch = useDispatch();
  const { loading } = useSelector((store) => store.transportation.transportHelper);
  const role = useSelector((store) => store.common.auth.role);
  const MYId = useSelector((store) => store.common.user?.userDetails?.userId);
  const { t } = useTranslation("admUserProfile");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNameTooltipVisible, setIsNameTooltipVisible] = useState(false);
  const [isPositionTooltipVisible, setIsPositionTooltipVisible] = useState(false);

  const adminStaffRole = role === "admin" || profile?.role === role;

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const deleteTeacher = () => {
    const userData = { id: profile._id, role: profile.role };
    dispatch(deactiveHelper(userData));
    setIsModalOpen(false);
  };

  const activateUser = (e, id, role) => {
    e.stopPropagation();
    const userData = { id: id, role, active: true };
    dispatch(activeHelper(userData));
  };

  const getContact = () => profile.contactNumber || profile.mobileNumber;

  const getDisplayName = () =>
    profile.fullName || `${profile.firstName || ""} ${profile.lastName || ""}`.trim();

  const isUserActive = (profile) => {
    if (typeof profile.active === "boolean") return profile.active;
    if (typeof profile.status === "string") return profile.status.toLowerCase() === "active";
    return false;
  };

  const getTruncatedName = (name, limit = 20) => {
    if (!name || name.length <= limit) return <span>{name}</span>;
    const truncated = name.slice(0, limit);
    return (
      <div className="relative inline-block">
        <span>{truncated}</span>
        <span
          className="text-blue-600 font-bold cursor-pointer ms-1"
          onMouseEnter={() => setIsNameTooltipVisible(true)}
          onMouseLeave={() => setIsNameTooltipVisible(false)}
          aria-label={`Show full name: ${name}`}
        >
          ...
        </span>
        <AnimatePresence>
          {isNameTooltipVisible && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute z-10 w-40 bg-white border border-gray-300 rounded-xl shadow-lg p-2 mt-1 end-0"
            >
              <p className="text-sm font-medium text-gray-800">{t("Full Name")}:</p>
              <p className="mt-1 text-gray-700 text-sm">{name}</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const getTruncatedPositions = (positions, limit = 1) => {
    if (!Array.isArray(positions) || positions.length === 0) {
      return "No positions assigned";
    }
    if (positions.length <= limit) return positions.join(", ");
    const visible = positions.slice(0, limit).join(", ");
    const remaining = positions.slice(limit);
    const remainingCount = remaining.length;
    return (
      <div className="relative inline-block">
        <span>{visible} </span>
        <span
          className="text-blue-600 font-bold cursor-pointer"
          onMouseEnter={() => setIsPositionTooltipVisible(true)}
          onMouseLeave={() => setIsPositionTooltipVisible(false)}
          aria-label={`Show ${remainingCount} more positions`}
        >
          +{remainingCount}
        </span>
        <AnimatePresence>
          {isPositionTooltipVisible && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute z-10 w-40 bg-white border border-gray-300 rounded-xl shadow-lg p-2 mt-1 end-0"
            >
              <p className="text-sm font-medium text-gray-800">{t("More Positions")}:</p>
              <ul className="mt-1 text-gray-700 text-sm list-disc list-inside">
                {remaining.map((pos, index) => (
                  <li key={index}>{pos}</li>
                ))}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  return (
    <div className="relative w-full sm:w-1/2 md:w-1/3 lg:w-1/4 px-3 mb-6">
      <div
        onClick={() => onClick(profile)}
        className="group relative bg-white rounded-3xl border border-gray-200 shadow-md hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 cursor-pointer overflow-hidden flex flex-col h-full"
      >
        {/* ── Cover band ───────────────────────── */}
        <div className="relative h-24 bg-gray-50 overflow-hidden flex-shrink-0">
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage:
                "radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)",
              backgroundSize: "32px 32px",
            }}
          />
        </div>

        {/* ── Deactivated label (original) ─────── */}
        {!isUserActive(profile) && adminStaffRole && (
          <span className="absolute top-3 left-4 z-20 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-white/90 text-red-500 border border-red-200 shadow-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
            {t("Deactivated")}
          </span>
        )}

        {/* ── Action buttons (original top-right column) ── */}
        {adminStaffRole && (
          <div className="absolute end-0 top-0 flex flex-col px-3 py-3 gap-2 z-20">
            {MYId == profile._id ? (
              <div className="bg-purple-100 text-purple-700 border border-purple-200 px-2 py-0.5 rounded-full text-[10px] font-bold">
                {t("My Profile")}
              </div>
            ) : (
              <>
                {isUserActive(profile) && (
                  <ProtectedAction requiredPermission={PERMISSIONS[`EDIT_${role.toUpperCase()}`]}>
                    <button
                      className="p-1.5 bg-white rounded-full shadow hover:bg-indigo-500 hover:text-white transition-all duration-200 text-slate-500"
                      onClick={(event) => editUser(event, profile)}
                      aria-label="Edit User"
                    >
                      {loading ? (
                        <FiLoader className="animate-spin w-3.5 h-3.5" />
                      ) : (
                        <CiEdit className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </ProtectedAction>
                )}

                {isUserActive(profile) ? (
                  <ProtectedAction requiredPermission={PERMISSIONS[`DEACTIVE_${role.toUpperCase()}`]}>
                    <button
                      className="p-1.5 bg-white rounded-full shadow hover:bg-red-500 hover:text-white transition-all duration-200 text-slate-500"
                      title={t("Deactivate")}
                      onClick={(event) => { event.stopPropagation(); openModal(); }}
                      aria-label="Deactivate User"
                    >
                      <MdBlock className="w-3.5 h-3.5" />
                    </button>
                  </ProtectedAction>
                ) : (
                  <ProtectedAction requiredPermission={PERMISSIONS[`ACTIVE_${role.toUpperCase()}`]}>
                    <button
                      className="p-1.5 bg-white rounded-full shadow hover:bg-green-500 hover:text-white transition-all duration-200 text-slate-500"
                      title={t("Activate")}
                      onClick={(e) => activateUser(e, profile._id, profile.role)}
                      aria-label="Activate User"
                    >
                      <MdOutlinePublishedWithChanges className="w-3.5 h-3.5" />
                    </button>
                  </ProtectedAction>
                )}
              </>
            )}
          </div>
        )}

        {/* ── Avatar overlapping cover ──────────── */}
        <div className="relative flex flex-col items-center -mt-12 z-10 px-6 pb-6">
          <div className="relative group-hover:scale-105 transition-transform duration-500">
            <div className="absolute -inset-1 rounded-full bg-gradient-to-tr from-violet-400 to-pink-400 opacity-0 group-hover:opacity-60 blur-sm transition-opacity duration-500" />
            <img
              className="relative w-24 h-24 rounded-full object-cover border-4 border-white shadow-xl z-10"
              src={profile?.photo || profileIcon}
              alt={getDisplayName()}
            />
          </div>

          {/* Role pill */}
          <span className="mt-2.5 px-3 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-purple-300 shadow-sm">
            {"Helper"}
          </span>

          {/* Name */}
          <h3 className="mt-2 text-base font-bold text-gray-900 text-center leading-snug w-full">
            {getTruncatedName(getDisplayName())}
          </h3>

          {/* Positions */}
          {role === "admin" && (
            <p className="text-xs text-gray-400 font-medium mt-1 text-center truncate w-full">
              {getTruncatedPositions(profile?.position)}
            </p>
          )}
        </div>

        {/* ── Footer ────────────────────────────── */}
        <div className="mt-auto border-t border-gray-100 px-6 py-3 flex items-center justify-between bg-gray-50 group-hover:bg-purple-50/40 transition-colors duration-500">
          <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
            Phone
          </span>
          <span className="text-sm font-semibold text-gray-700 truncate max-w-[130px]">
            {getContact() || "—"}
          </span>
        </div>
      </div>

      {!loading && adminStaffRole && (
        <DeleteConfirmatiomModal
          isOpen={isModalOpen}
          onClose={closeModal}
          onConfirm={deleteTeacher}
          text={"Deactivate"}
        />
      )}
    </div>
  );
};

export default ProfileHelperCard;